import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import SearchModal from '@/components/wiki/SearchModal'
import {
  ARMORY_PUBLIC_URL,
  PLAYER_HUB_URL,
} from '@/config/externalLinks'
import { useWikiNav } from '@/components/layout/wikiNavContext'

const ecosystemButtonBase =
  'ecosystem-cta-button inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b783ff]/55 focus-visible:ring-offset-2 focus-visible:ring-offset-calamity-header'
const ecosystemButtonDesktop = 'min-w-[120px]'
const ecosystemButtonMobile = 'w-full min-w-0'

export default function Header() {
  const [searchOpen, setSearchOpen] = useState(false)
  const navigate = useNavigate()
  const { toggleMobileNav, mobileNavOpen } = useWikiNav()
  const headerRef = useRef<HTMLElement | null>(null)

  const ecosystemLinks = [
    { href: ARMORY_PUBLIC_URL, label: 'Armory' },
    { href: PLAYER_HUB_URL, label: 'Player Hub' },
  ]

  useLayoutEffect(() => {
    const el = headerRef.current
    if (!el) return

    const root = document.documentElement
    const update = () => {
      const h = el.getBoundingClientRect().height
      if (!Number.isFinite(h) || h <= 0) return
      // Preserve fractional pixels to avoid "sticky" panels shifting a few px on first scroll.
      const next = `${Math.round(h * 10) / 10}px`
      root.style.setProperty('--wiki-header-h', next)
    }

    update()

    let ro: ResizeObserver | null = null
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => update())
        ro.observe(el)
      }
    } catch { }

    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('resize', update)
      try { ro?.disconnect() } catch { }
    }
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <>
      <header ref={headerRef} className="ecosystem-header-shell sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <button
                onClick={toggleMobileNav}
                className="ecosystem-control-surface lg:hidden motion-safe:animate-nav-slide-in flex items-center justify-center w-10 h-10 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b783ff]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-calamity-header"
                aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={mobileNavOpen}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <Link to="/" className="flex items-center gap-3 min-w-0">
                <img src="/static/calamityicon.png" alt="Calamity" className="w-10 h-10 pixelated shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] uppercase tracking-[0.28em] text-calamity-text-dim">
                    Calamity
                  </span>
                  <span className="text-xl sm:text-2xl font-display text-calamity-text leading-tight truncate">
                    Wiki
                  </span>
                </div>
              </Link>
            </div>

            <nav className="hidden lg:flex flex-1 items-center justify-center min-w-0 px-2">
              <div className="inline-flex items-center gap-2">
                {ecosystemLinks.map((entry) => (
                  <a
                    key={entry.label}
                    href={entry.href}
                    title={`Open ${entry.label}`}
                    className={`${ecosystemButtonBase} ${ecosystemButtonDesktop}`}
                  >
                    {entry.label}
                  </a>
                ))}
              </div>
            </nav>

            <div className="flex items-center justify-end gap-2 flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="ecosystem-control-surface sm:hidden flex items-center justify-center h-10 w-10 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b783ff]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-calamity-header"
                aria-label="Open search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="ecosystem-control-surface hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 text-sm w-full max-w-[280px] md:max-w-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b783ff]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-calamity-header"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Search...</span>
                <kbd className="hidden md:inline-flex ml-auto text-[10px] px-1.5 py-0.5 rounded bg-calamity-panel border border-calamity-border-subtle text-calamity-text-muted">
                  Ctrl+K
                </kbd>
              </button>
            </div>
          </div>

          <nav className="mt-3 lg:hidden">
            <div className="grid grid-cols-2 gap-2">
              {ecosystemLinks.map((entry) => (
                <a
                  key={`mobile-${entry.label}`}
                  href={entry.href}
                  title={`Open ${entry.label}`}
                  className={`${ecosystemButtonBase} ${ecosystemButtonMobile}`}
                >
                  {entry.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </header>

      {searchOpen && (
        <SearchModal
          onClose={() => setSearchOpen(false)}
          onNavigate={(slug) => {
            setSearchOpen(false)
            navigate(`/${slug}`)
          }}
        />
      )}
    </>
  )
}
